import React from 'react';
import { Button, Typography, Box, Container } from '@mui/material';
import { BiSolidUserBadge } from 'react-icons/bi';
import { useRooftop } from '@store';
import { ADMIN5_URL } from '../config.frontend';

/**
 * Shown to HopDrive Admin users who land on dealer-portal user/driver management.
 *
 * Admins can see every customer in the system, so the dealer portal's rooftop-scoped
 * user management isn't the right surface for them — staff manage users in the Admin
 * Portal (Admin 5) instead. We block the route rather than render a half-scoped view.
 *
 * Deep-links to the selected rooftop's customer in Admin 5 when one is selected.
 * Admin 5's `customers/:id` route redirects to the org-scoped users page
 * (`organizations/:orgId/customer/:id/users`), so we don't need the org ID here.
 */
const AdminManageInAdminPortalPage: React.FC = () => {
  const rooftop = useRooftop();
  const target = ADMIN5_URL
    ? rooftop?.id
      ? `${ADMIN5_URL}customers/${rooftop.id}`
      : ADMIN5_URL
    : '';

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        textAlign="center"
        gap={3}
      >
        <BiSolidUserBadge size={64} color="#ff4830" />

        <Typography variant="h4" component="h1" gutterBottom>
          Manage users in Admin 5
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          You're signed in as a HopDrive Admin. Admin accounts can see every customer in
          the system, so user and driver management for staff lives in the Admin Portal.
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          Please manage users and drivers in <strong>Admin 5</strong>.
        </Typography>

        <Button
          variant="contained"
          color="primary"
          href={target}
          size="large"
          disabled={!target}
        >
          Go to Admin 5
        </Button>
      </Box>
    </Container>
  );
};

export default AdminManageInAdminPortalPage;
